// AuthPage.js - полная версия с диагностикой и восстановлением пароля

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    User,
    Mail,
    Lock,
    Eye,
    EyeOff,
    UserPlus,
    LogIn,
    AlertCircle,
    CheckCircle,
    Phone,
    MessageCircle,
    Users,
    Wifi,
    WifiOff,
    RefreshCw,
    KeyRound  // ДОБАВЛЕНО: иконка для восстановления пароля
} from 'lucide-react';
import { authService } from '../services/authService';
import { FieldError, SuccessMessage, ErrorMessage } from '../components/ValidationHelper';

// Диагностический компонент (встроенный)
// Диагностический компонент БЕЗ health проверки
const NetworkDiagnostics = ({ onRetry }) => {
    const [diagnostics, setDiagnostics] = useState({
        isOnline: navigator.onLine,
        serverStatus: 'unknown',
        apiUrl: process.env.REACT_APP_API_URL || 'https://api.light-platform.com/api/v1',
        lastCheck: null,
        errors: []
    });

    const [isChecking, setIsChecking] = useState(false);

    const checkServerStatus = async () => {
        setIsChecking(true);
        const errors = [];

        try {
            // Пробуем сделать запрос напрямую к check-referral с тестовым кодом
            const testResponse = await fetch(`${diagnostics.apiUrl}/auth/check-referral`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify({ referral_code: 'Admin' })
            });

            // Если статус 200 (успех) или 404 (код не найден) - сервер работает
            if (testResponse.ok || testResponse.status === 404) {
                setDiagnostics(prev => ({
                    ...prev,
                    serverStatus: 'online',
                    lastCheck: new Date().toLocaleTimeString(),
                    errors: []
                }));
                setIsChecking(false);
                return;
            } else {
                errors.push(`Сервер недоступен: ${testResponse.status}`);
            }
        } catch (error) {
            errors.push(`Ошибка подключения: ${error.message}`);
        }

        setDiagnostics(prev => ({
            ...prev,
            serverStatus: errors.length === 0 ? 'online' : 'offline',
            lastCheck: new Date().toLocaleTimeString(),
            errors: errors
        }));

        setIsChecking(false);
    };

    useEffect(() => {
        checkServerStatus();
    }, []);

    const getStatusIcon = () => {
        if (!diagnostics.isOnline) return <WifiOff className="text-red-500" size={16} />;
        if (diagnostics.serverStatus === 'online') return <CheckCircle className="text-green-500" size={16} />;
        if (diagnostics.serverStatus === 'checking') return <RefreshCw className="animate-spin text-blue-500" size={16} />;
        return <AlertCircle className="text-red-500" size={16} />;
    };

    const getStatusText = () => {
        if (!diagnostics.isOnline) return 'Нет интернета';
        if (diagnostics.serverStatus === 'online') return 'Соединение работает';
        if (diagnostics.serverStatus === 'checking') return 'Проверка...';
        return 'Проблемы с сервером';
    };

    return (
        <div className="bg-gray-50 border rounded-lg p-3 mb-4 text-sm">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                    {getStatusIcon()}
                    <span className="font-medium">{getStatusText()}</span>
                </div>
                <button
                    onClick={() => {
                        checkServerStatus();
                        if (onRetry) onRetry();
                    }}
                    disabled={isChecking}
                    className="text-blue-primary hover:text-blue-600 disabled:opacity-50 flex items-center space-x-1"
                >
                    <RefreshCw className={isChecking ? 'animate-spin' : ''} size={14} />
                    <span>Проверить</span>
                </button>
            </div>

            {diagnostics.errors.length > 0 && (
                <div className="mt-2 space-y-1">
                    {diagnostics.errors.map((error, index) => (
                        <div key={index} className="text-red-600 text-xs flex items-start">
                            <span className="mr-1">•</span>
                            <span>{error}</span>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-2 pt-2 border-t text-xs text-gray-500 space-y-1">
                <div>API: {diagnostics.apiUrl}</div>
                <div>Браузер: {/Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'Мобильный' : 'Десктоп'}</div>
                {diagnostics.lastCheck && <div>Проверено: {diagnostics.lastCheck}</div>}
            </div>
        </div>
    );
};

// НОВЫЙ КОМПОНЕНТ: Модальное окно восстановления пароля
const PasswordRecoveryModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
    const [recoveryData, setRecoveryData] = useState({
        identifier: '',
        recoveryMethod: 'both'
    });
    const [errors, setErrors] = useState({});

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!recoveryData.identifier.trim()) {
            newErrors.identifier = 'Введите email или логин';
        }

        if (Object.keys(newErrors).length === 0) {
            onSubmit(recoveryData);
            setRecoveryData({ identifier: '', recoveryMethod: 'both' });
            setErrors({});
        } else {
            setErrors(newErrors);
        }
    };

    const handleClose = () => {
        setRecoveryData({ identifier: '', recoveryMethod: 'both' });
        setErrors({});
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center">
                        <KeyRound className="mr-2 text-blue-primary" size={24} />
                        Восстановление пароля
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                        disabled={isLoading}
                    >
                        ×
                    </button>
                </div>

                <p className="text-gray-600 mb-6 text-sm">
                    Введите ваш email или логин. Мы отправим инструкции по восстановлению пароля на вашу почту и в Telegram (если подключён).
                </p>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Mail size={16} className="inline mr-1" />
                            Email или логин
                        </label>
                        <input
                            type="text"
                            value={recoveryData.identifier}
                            onChange={(e) => {
                                setRecoveryData({ ...recoveryData, identifier: e.target.value });
                                setErrors({});
                            }}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors ${
                                errors.identifier ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Введите email или логин"
                            disabled={isLoading}
                        />
                        <FieldError error={errors.identifier} />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Способ восстановления
                        </label>
                        <div className="space-y-2">
                            <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                                <input
                                    type="radio"
                                    name="recoveryMethod"
                                    value="both"
                                    checked={recoveryData.recoveryMethod === 'both'}
                                    onChange={(e) => setRecoveryData({ ...recoveryData, recoveryMethod: e.target.value })}
                                    className="mr-3 w-4 h-4"
                                    disabled={isLoading}
                                />
                                <div className="flex items-center">
                                    <Mail size={18} className="mr-2 text-blue-primary" />
                                    <MessageCircle size={18} className="mr-2 text-blue-primary" />
                                    <span className="font-medium">Email и Telegram</span>
                                </div>
                            </label>
                            <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                                <input
                                    type="radio"
                                    name="recoveryMethod"
                                    value="email"
                                    checked={recoveryData.recoveryMethod === 'email'}
                                    onChange={(e) => setRecoveryData({ ...recoveryData, recoveryMethod: e.target.value })}
                                    className="mr-3 w-4 h-4"
                                    disabled={isLoading}
                                />
                                <div className="flex items-center">
                                    <Mail size={18} className="mr-2 text-blue-primary" />
                                    <span className="font-medium">Только Email</span>
                                </div>
                            </label>
                            <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                                <input
                                    type="radio"
                                    name="recoveryMethod"
                                    value="telegram"
                                    checked={recoveryData.recoveryMethod === 'telegram'}
                                    onChange={(e) => setRecoveryData({ ...recoveryData, recoveryMethod: e.target.value })}
                                    className="mr-3 w-4 h-4"
                                    disabled={isLoading}
                                />
                                <div className="flex items-center">
                                    <MessageCircle size={18} className="mr-2 text-blue-primary" />
                                    <span className="font-medium">Только Telegram</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                            disabled={isLoading}
                        >
                            Отмена
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-primary to-mint-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 font-medium"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Отправка...
                                </div>
                            ) : (
                                'Восстановить'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AuthPage = ({ onLogin }) => {
    const [isLogin, setIsLogin] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState(null);
    const [showDiagnostics, setShowDiagnostics] = useState(false);

    // ДОБАВЛЕНО: состояния для восстановления пароля
    const [showRecoveryModal, setShowRecoveryModal] = useState(false);
    const [isRecoveryLoading, setIsRecoveryLoading] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    // Данные для входа
    const [loginData, setLoginData] = useState({
        login: '',
        password: ''
    });

    // Данные для регистрации
    const [registerData, setRegisterData] = useState({
        email: '',
        login: '',
        password: '',
        password_confirmation: '',
        first_name: '',
        last_name: '',
        phone: '',
        telegram_login: '',
        agree_to_terms: false
    });

    // Реферальная информация
    const [referralInfo, setReferralInfo] = useState({
        code: '',
        inviterName: '',
        hasValidReferral: false
    });

    // Извлечение и проверка реферального кода
    // Извлечение и проверка реферального кода
    useEffect(() => {
        const extractReferralCode = async () => {
            try {
                const params = new URLSearchParams(location.search);
                const refCode = params.get('ref') || params.get('referral');

                // ✅ ВАЖНО: Сбрасываем предыдущие данные при каждом изменении URL
                setReferralInfo({
                    code: '',
                    inviterName: '',
                    inviterData: null,
                    hasValidReferral: false
                });
                setNotification(null);
                setShowDiagnostics(false);

                if (!refCode) {
                    console.log('No referral code found in URL');
                    setNotification({
                        type: 'info',
                        message: 'Регистрация доступна только по реферальной ссылке. Получите реферальную ссылку от действующего участника.'
                    });
                    return;
                }

                // Проверяем новый реферальный код
                await checkReferralCode(refCode);
            } catch (error) {
                console.error('Error extracting referral code:', error);
                setNotification({
                    type: 'error',
                    message: 'Ошибка обработки реферальной ссылки'
                });
            }
        };

        extractReferralCode();
    }, [location.search]); // ← Реагирует на изменения URL

    // Альтернативный метод проверки через XMLHttpRequest
    const checkReferralCodeXHR = (code) => {
        return new Promise((resolve) => {
            const xhr = new XMLHttpRequest();
            const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/auth/check-referral`;

            xhr.open('POST', url, true);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.setRequestHeader('Accept', 'application/json');
            xhr.timeout = 15000;

            xhr.onload = function() {
                try {
                    const data = JSON.parse(xhr.responseText);
                    if (xhr.status === 200 && data.success) {
                        resolve({ success: true, data: data.data });
                    } else {
                        resolve({ success: false, message: data.message || 'Ошибка проверки' });
                    }
                } catch (e) {
                    resolve({ success: false, message: 'Ошибка парсинга ответа' });
                }
            };

            xhr.onerror = function() {
                resolve({ success: false, message: 'Ошибка сети (XHR)' });
            };

            xhr.ontimeout = function() {
                resolve({ success: false, message: 'Превышено время ожидания (XHR)' });
            };

            xhr.send(JSON.stringify({ referral_code: code }));
        });
    };

    const checkReferralCode = async (code) => {
        if (!code || !code.trim()) {
            setReferralInfo({
                code: '',
                inviterName: '',
                inviterData: null,
                hasValidReferral: false
            });
            return;
        }

        console.log('Checking referral code:', code);

        try {
            setNotification({
                type: 'info',
                message: 'Проверяем реферальную ссылку...'
            });

            // Метод 1: Основной сервис
            let response = await authService.checkReferralCode(code);

            // Метод 2: Если не удалось, пробуем XMLHttpRequest
            if (!response.success) {
                console.log('Trying XHR fallback...');
                response = await checkReferralCodeXHR(code);
            }

            if (response.success && response.data) {
                // ✅ ВАЖНО: Сохраняем ВСЕ данные реферера
                setReferralInfo({
                    code: code,
                    inviterName: response.data.full_name ||
                        `${response.data.first_name || ''} ${response.data.last_name || ''}`.trim() ||
                        response.data.login,
                    inviterData: response.data,  // Полный объект
                    hasValidReferral: true
                });

                setNotification({
                    type: 'success',
                    message: `Вас пригласил: ${response.data.full_name || response.data.login}`
                });
                setShowDiagnostics(false);

                console.log('Referral data saved:', response.data);
            } else {
                // ❌ Очищаем данные при неудаче
                setReferralInfo({
                    code: code,
                    inviterName: '',
                    inviterData: null,
                    hasValidReferral: false
                });

                setNotification({
                    type: 'error',
                    message: response.message || 'Недействительная реферальная ссылка'
                });
                setShowDiagnostics(true);
            }
        } catch (error) {
            console.error('All referral check methods failed:', error);

            // ❌ Очищаем данные при ошибке
            setReferralInfo({
                code: code,
                inviterName: '',
                inviterData: null,
                hasValidReferral: false
            });

            setNotification({
                type: 'error',
                message: 'Ошибка проверки реферальной ссылки. Проверьте подключение к интернету.'
            });
            setShowDiagnostics(true);
        }
    };

    // Обработчики изменения полей
    const handleLoginChange = (e) => {
        const { name, value } = e.target;
        setLoginData(prev => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleRegisterChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setRegisterData(prev => ({ ...prev, [name]: newValue }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    // Валидация формы входа
    const validateLoginForm = () => {
        const newErrors = {};

        if (!loginData.login.trim()) {
            newErrors.login = 'Введите логин или email';
        }

        if (!loginData.password.trim()) {
            newErrors.password = 'Введите пароль';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Валидация формы регистрации
    const validateRegisterForm = () => {
        const newErrors = {};

        if (!referralInfo.hasValidReferral) {
            newErrors.referral = 'Регистрация доступна только по действительной реферальной ссылке';
        }

        if (!registerData.email.trim()) {
            newErrors.email = 'Email обязателен для заполнения';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerData.email)) {
            newErrors.email = 'Некорректный формат email';
        }

        if (!registerData.login.trim()) {
            newErrors.login = 'Логин обязателен для заполнения';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(registerData.login)) {
            newErrors.login = 'Логин может содержать только английские буквы, цифры, дефис и подчеркивание';
        } else if (registerData.login.length < 3) {
            newErrors.login = 'Логин должен содержать минимум 3 символа';
        }

        if (!registerData.password.trim()) {
            newErrors.password = 'Пароль обязателен для заполнения';
        } else if (registerData.password.length < 8) {
            newErrors.password = 'Пароль должен содержать минимум 8 символов';
        }

        if (!registerData.password_confirmation.trim()) {
            newErrors.password_confirmation = 'Подтвердите пароль';
        } else if (registerData.password !== registerData.password_confirmation) {
            newErrors.password_confirmation = 'Пароли не совпадают';
        }

        if (registerData.phone.trim() && !/^\+?[1-9]\d{1,14}$/.test(registerData.phone.replace(/\s/g, ''))) {
            newErrors.phone = 'Введите корректный номер телефона в международном формате';
        }

        if (registerData.telegram_login.trim() && !/^[a-zA-Z0-9_]+$/.test(registerData.telegram_login)) {
            newErrors.telegram_login = 'Telegram логин может содержать только английские буквы, цифры и подчеркивание';
        } else if (registerData.telegram_login.trim() && registerData.telegram_login.length < 5) {
            newErrors.telegram_login = 'Telegram логин должен содержать минимум 5 символов';
        }

        if (!registerData.agree_to_terms) {
            newErrors.agree_to_terms = 'Необходимо согласие с правилами';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // НОВАЯ ФУНКЦИЯ: Обработка восстановления пароля
    const handlePasswordRecovery = async (recoveryData) => {
        setIsRecoveryLoading(true);

        try {
            // ИСПРАВЛЕНО: передаем параметры раздельно
            const response = await authService.requestPasswordRecovery(
                recoveryData.identifier,     // Первый параметр - строка
                recoveryData.recoveryMethod  // Второй параметр - строка
            );

            setIsRecoveryLoading(false);
            setShowRecoveryModal(false);

            if (response.success) {
                setNotification({
                    type: 'success',
                    message: response.message
                });
            } else {
                setNotification({
                    type: 'error',
                    message: response.message
                });
            }
        } catch (error) {
            setIsRecoveryLoading(false);
            setShowRecoveryModal(false);
            setNotification({
                type: 'error',
                message: 'Ошибка при отправке запроса на восстановление пароля'
            });
        }
    };

    // Отправка формы
    const handleSubmit = async (e) => {
        e.preventDefault();

        const isValid = isLogin ? validateLoginForm() : validateRegisterForm();

        if (!isValid) {
            return;
        }

        setIsLoading(true);

        try {
            let response;

            if (isLogin) {
                response = await authService.login({
                    login: loginData.login.trim(),
                    password: loginData.password
                });
            } else {
                const registrationData = {
                    email: registerData.email.trim(),
                    login: registerData.login.trim(),
                    password: registerData.password,
                    password_confirmation: registerData.password_confirmation
                };

                if (registerData.first_name.trim()) {
                    registrationData.first_name = registerData.first_name.trim();
                }
                if (registerData.last_name.trim()) {
                    registrationData.last_name = registerData.last_name.trim();
                }
                if (registerData.phone.trim()) {
                    registrationData.phone = registerData.phone.trim();
                }
                if (registerData.telegram_login.trim()) {
                    registrationData.telegram_login = registerData.telegram_login.trim();
                }

                registrationData.referral_code = referralInfo.code;

                console.log('Sending registration data:', registrationData);

                response = await authService.register(registrationData);
            }

            console.log('Auth response:', response);

            if (response.success) {
                setNotification({
                    type: 'success',
                    message: isLogin ? 'Вход выполнен успешно!' : 'Регистрация завершена успешно!'
                });

                if (response.data.access_token || response.data.token) {
                    localStorage.setItem('auth_token', response.data.access_token || response.data.token);
                }

                if (onLogin) {
                    onLogin(response.data.user || response.data);
                }

                setTimeout(() => {
                    navigate('/dashboard');
                }, 1000);

            } else {
                // Обработка ошибок от сервера
                if (response.errors) {
                    // Преобразуем массивы ошибок в строки
                    const formattedErrors = {};
                    Object.keys(response.errors).forEach(key => {
                        if (Array.isArray(response.errors[key])) {
                            formattedErrors[key] = response.errors[key][0]; // Берём первую ошибку из массива
                        } else {
                            formattedErrors[key] = response.errors[key];
                        }
                    });
                    setErrors(formattedErrors);

                    // Показываем общее сообщение об ошибке
                    setNotification({
                        type: 'error',
                        message: response.message || 'Проверьте правильность введённых данных'
                    });
                } else {
                    setNotification({
                        type: 'error',
                        message: response.message || 'Произошла ошибка'
                    });
                    setShowDiagnostics(true);
                }
            }

        } catch (error) {
            console.error('Auth error:', error);
            setNotification({
                type: 'error',
                message: error.message || 'Произошла ошибка при отправке данных. Проверьте подключение к интернету.'
            });
            setShowDiagnostics(true);
        } finally {
            setIsLoading(false);
        }
    };

    // Функция для повторной проверки реферального кода
    const retryReferralCheck = () => {
        if (referralInfo.code) {
            checkReferralCode(referralInfo.code);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-mint-light to-white flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">

                {/* Логотип */}
                <div className="text-center mb-8">
                    <div className="w-32 h-auto mx-auto mb-4 ">
                        <img
                            src="/images/logo.png"
                            alt="ЛАЙТ логотип"
                            className="w-full h-auto"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-800">
                        {isLogin ? 'Вход в систему' : 'Регистрация'}
                    </h1>
                    <p className="text-gray-600 mt-2">
                        {isLogin ? 'Добро пожаловать обратно!' : 'Создайте новый аккаунт'}
                    </p>
                </div>

                {/* Отладочная информация (только для разработки) */}
                {process.env.NODE_ENV === 'development' && (
                    <div className="mb-4 p-3 bg-gray-100 rounded-lg text-xs">
                        <div className="font-medium mb-1">Отладка:</div>
                        <div>Код: {referralInfo.code}</div>
                        <div>Валидный: {referralInfo.hasValidReferral ? 'Да' : 'Нет'}</div>
                        <div>URL: {location.search}</div>
                        <div>API: {process.env.REACT_APP_API_URL}</div>
                        <div>Онлайн: {navigator.onLine ? 'Да' : 'Нет'}</div>
                        <button
                            onClick={async () => {
                                try {
                                    // Прямой запрос к check-referral вместо health
                                    const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.light-platform.com/api/v1'}/auth/check-referral`, {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            'Accept': 'application/json'
                                        },
                                        body: JSON.stringify({ referral_code: referralInfo.code || 'Admin' })
                                    });

                                    const data = await response.json();
                                    alert(JSON.stringify({
                                        status: response.status,
                                        ok: response.ok,
                                        data: data
                                    }, null, 2));
                                } catch (error) {
                                    alert(`Ошибка: ${error.message}`);
                                }
                            }}
                            className="mt-1 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                        >
                            Тест API
                        </button>
                    </div>
                )}

                {/* Диагностика сети - показываем при ошибках */}
                {showDiagnostics && (
                    <NetworkDiagnostics onRetry={retryReferralCheck} />
                )}

                {/* Уведомления */}
                {notification && (
                    <div className="mb-4">
                        {notification.type === 'success' ? (
                            <SuccessMessage
                                message={notification.message}
                                onClose={() => setNotification(null)}
                            />
                        ) : notification.type === 'info' ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <AlertCircle className="text-blue-500 mr-3 flex-shrink-0" size={20} />
                                    <p className="text-blue-800">{notification.message}</p>
                                </div>
                            </div>
                        ) : (
                            <ErrorMessage
                                message={notification.message}
                                onClose={() => setNotification(null)}
                            />
                        )}
                    </div>
                )}

                {/* Форма */}
                {(referralInfo.hasValidReferral || isLogin) ? (
                    <form onSubmit={handleSubmit}>
                        {isLogin ? (
                            // Форма входа
                            <>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <User size={16} className="inline mr-1" />
                                        Логин или Email *
                                    </label>
                                    <input
                                        type="text"
                                        name="login"
                                        value={loginData.login}
                                        onChange={handleLoginChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors ${
                                            errors.login ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Введите логин или email"
                                        disabled={isLoading}
                                    />
                                    <FieldError error={errors.login} />
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Lock size={16} className="inline mr-1" />
                                        Пароль *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={loginData.password}
                                            onChange={handleLoginChange}
                                            className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors ${
                                                errors.password ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="Введите пароль"
                                            disabled={isLoading}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                    <FieldError error={errors.password} />
                                </div>

                                {/* ДОБАВЛЕНО: Ссылка "Забыли пароль?" */}
                                <div className="mb-6 text-right">
                                    <button
                                        type="button"
                                        onClick={() => setShowRecoveryModal(true)}
                                        className="text-sm text-blue-primary hover:text-blue-600 hover:underline font-medium inline-flex items-center"
                                    >
                                        <KeyRound size={14} className="mr-1" />
                                        Забыли пароль?
                                    </button>
                                </div>
                            </>
                        ) : (
                            // Форма регистрации (все ваши существующие поля)
                            <>
                                {/* Email */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Mail size={16} className="inline mr-1" />
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={registerData.email}
                                        onChange={handleRegisterChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors ${
                                            errors.email ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Введите email"
                                        disabled={isLoading}
                                    />
                                    <FieldError error={errors.email} />
                                </div>

                                {/* Логин */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <User size={16} className="inline mr-1" />
                                        Логин *
                                    </label>
                                    <input
                                        type="text"
                                        name="login"
                                        value={registerData.login}
                                        onChange={handleRegisterChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors ${
                                            errors.login ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Только английские буквы, цифры, - и _"
                                        disabled={isLoading}
                                    />
                                    <FieldError error={errors.login} />
                                </div>

                                {/* Пароль */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Lock size={16} className="inline mr-1" />
                                        Пароль *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={registerData.password}
                                            onChange={handleRegisterChange}
                                            className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors ${
                                                errors.password ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="Минимум 8 символов"
                                            disabled={isLoading}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                    <FieldError error={errors.password} />
                                </div>

                                {/* Подтверждение пароля */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Lock size={16} className="inline mr-1" />
                                        Подтверждение пароля *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            name="password_confirmation"
                                            value={registerData.password_confirmation}
                                            onChange={handleRegisterChange}
                                            className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors ${
                                                errors.password_confirmation ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="Повторите пароль"
                                            disabled={isLoading}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                    <FieldError error={errors.password_confirmation} />
                                </div>

                                {/* Имя (опционально) */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <User size={16} className="inline mr-1" />
                                        Имя
                                    </label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={registerData.first_name}
                                        onChange={handleRegisterChange}
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors border-gray-300"
                                        placeholder="Введите имя"
                                        disabled={isLoading}
                                    />
                                </div>

                                {/* Фамилия (опционально) */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <User size={16} className="inline mr-1" />
                                        Фамилия
                                    </label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={registerData.last_name}
                                        onChange={handleRegisterChange}
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors border-gray-300"
                                        placeholder="Введите фамилию"
                                        disabled={isLoading}
                                    />
                                </div>

                                {/* Телефон (опционально) */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Phone size={16} className="inline mr-1" />
                                        Телефон
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={registerData.phone}
                                        onChange={handleRegisterChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors ${
                                            errors.phone ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="+7 (XXX) XXX-XX-XX"
                                        disabled={isLoading}
                                    />
                                    <FieldError error={errors.phone} />
                                </div>

                                {/* Telegram логин (опционально) */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <MessageCircle size={16} className="inline mr-1" />
                                        Telegram логин
                                    </label>
                                    <input
                                        type="text"
                                        name="telegram_login"
                                        value={registerData.telegram_login}
                                        onChange={handleRegisterChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-primary focus:border-transparent transition-colors ${
                                            errors.telegram_login ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="@username"
                                        disabled={isLoading}
                                    />
                                    <FieldError error={errors.telegram_login} />
                                </div>

                                {/* Согласие с правилами */}
                                <div className="mb-6">
                                    <label className="flex items-start">
                                        <input
                                            type="checkbox"
                                            name="agree_to_terms"
                                            checked={registerData.agree_to_terms}
                                            onChange={handleRegisterChange}
                                            className="mt-1 mr-2 w-4 h-4"
                                            disabled={isLoading}
                                        />
                                        <span className="text-sm text-gray-600">
                                            Я согласен с{' '}
                                            <a href="/terms" className="text-blue-primary hover:underline">
                                                правилами и условиями
                                            </a>
                                            {' '}использования платформы ЛАЙТ
                                        </span>
                                    </label>
                                    <FieldError error={errors.agree_to_terms} />
                                </div>
                            </>
                        )}

                        {/* Кнопка отправки */}
                        {referralInfo.hasValidReferral || isLogin ? (
                            <button
                                type="submit"
                                className="w-full py-3 bg-gradient-to-r from-blue-primary to-mint-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 font-medium"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                        {isLogin ? 'Вход...' : 'Регистрация...'}
                                    </div>
                                ) : (
                                    <>
                                        {isLogin ? (
                                            <>
                                                <LogIn size={20} className="inline mr-2" />
                                                Войти
                                            </>
                                        ) : (
                                            <>
                                                <UserPlus size={20} className="inline mr-2" />
                                                Зарегистрироваться
                                            </>
                                        )}
                                    </>
                                )}
                            </button>
                        ) : null}
                    </form>
                ) : (
                    // Сообщение о том, что нужна реферальная ссылка
                    <div className="text-center py-8">
                        <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            Нужно приглашение
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Регистрация в ЛАЙТ доступна только по персональным приглашениям от действующих участников.
                        </p>
                        <p className="text-sm text-gray-500">
                            Получите реферальную ссылку от вашего знакомого, который уже является участником платформы.
                        </p>
                    </div>
                )}

                {/* Дополнительные ссылки */}
                {(referralInfo.hasValidReferral || isLogin) && (
                    <div className="mt-6 text-center">
                        <p className="text-sm text-gray-600">
                            {isLogin ? 'Нет аккаунта?' : 'Уже есть аккаунт?'}
                            {' '}
                            <button
                                type="button"
                                onClick={() => setIsLogin(!isLogin)}
                                className="text-blue-primary hover:underline font-medium"
                                disabled={isLoading}
                            >
                                {isLogin ? 'Зарегистрироваться' : 'Войти'}
                            </button>
                        </p>
                    </div>
                )}

                {/* Информация о необязательных полях */}
                {!isLogin && referralInfo.hasValidReferral && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-700">
                            <AlertCircle size={14} className="inline mr-1" />
                            Поля отмеченные * обязательны для заполнения.
                            Остальные поля можно заполнить позже в личном кабинете.
                        </p>
                    </div>
                )}
            </div>

            {/* ДОБАВЛЕНО: Модальное окно восстановления пароля */}
            <PasswordRecoveryModal
                isOpen={showRecoveryModal}
                onClose={() => setShowRecoveryModal(false)}
                onSubmit={handlePasswordRecovery}
                isLoading={isRecoveryLoading}
            />
        </div>
    );
};

export default AuthPage;
